import apiEndPoints from "../../../routeConfig";
import { ApiService } from "../../../../service/api.service";

const apiService = new ApiService();

/**
 * The sheet is the most expensive read in the admin app — it aggregates every user, farmer and
 * exchange in a country against its whole administrative hierarchy — and the screen asks for it
 * far more often than the data changes. Drilling into a state and pressing Back used to re-run the
 * whole aggregation for a sheet the user had just been looking at.
 *
 * So sheets are held for a few minutes and served from memory. The window is short enough that an
 * onboarding that happened while the screen was open still shows up on the next visit, and long
 * enough that walking up and down the hierarchy costs one request per level rather than one per
 * click. Switching country or reloading the page is unaffected — a reload starts an empty cache.
 */
const SHEET_TTL_MS = 5 * 60 * 1000;

const sheetCache = new Map();
/**
 * Requests still in flight, so the same sheet asked for twice before the first answer arrives is
 * one request. React's development-mode double effect and an impatient double click both land here.
 */
const inFlight = new Map();

const cacheKey = (countryId, locationId) => `${countryId}|${locationId || ''}`;

/** Drops everything cached. Called when the answer would be stale for a reason time cannot fix. */
export const clearAnalysisCache = () => {
  sheetCache.clear();
  inFlight.clear();
};

let countriesPromise = null;

export const listAnalyticsCountries = async () => {
  // The picker is the same list for the life of the session and is asked for on every mount.
  if (countriesPromise) return countriesPromise;
  countriesPromise = (async () => {
    try {
      const response = await apiService.get(apiEndPoints.analyticsCountries);
      return { statusCode: 200, data: response.data };
    } catch (error) {
      // A failure must not be remembered, or the picker stays broken until a reload.
      countriesPromise = null;
      return {
        statusCode: error?.response?.status || 500,
        message: error?.response?.data?.message || 'Failed to load countries',
      };
    }
  })();
  return countriesPromise;
};

/**
 * The sheet for one scope. With no `locationId` that is the country and its top tier; with one it is
 * that location and the tier below it — the drill-down. Both answer the same shape, so the screen
 * renders one set of panels no matter how deep it has gone.
 *
 * Resolves with `cached: true` when the answer came from memory, which lets the caller render it
 * without the loading state — dimming the sheet for a render that is already available reads as a
 * delay that is not happening.
 */
export const getAnalysisSheet = async (countryId, locationId, { force = false } = {}) => {
  const key = cacheKey(countryId, locationId);

  if (!force) {
    const hit = sheetCache.get(key);
    if (hit && Date.now() - hit.storedAt < SHEET_TTL_MS) {
      return { statusCode: 200, data: hit.data, cached: true };
    }
    const pending = inFlight.get(key);
    if (pending) return pending;
  }

  const url = locationId
    ? `${apiEndPoints.analyticsCountries}/${countryId}/locations/${locationId}`
    : `${apiEndPoints.analyticsCountries}/${countryId}`;

  const request = (async () => {
    try {
      const response = await apiService.get(url);
      sheetCache.set(key, { storedAt: Date.now(), data: response.data });
      return { statusCode: 200, data: response.data };
    } catch (error) {
      return {
        statusCode: error?.response?.status || 500,
        message: error?.response?.data?.message || 'Failed to load country analysis',
      };
    } finally {
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, request);
  return request;
};
